import API_CONFIG from '../config/api.config';
import apiClient from './apiClient';
import { EVENTS } from '../features/events/data';

const delay = (ms = API_CONFIG.MOCK_DELAY) => new Promise(r => setTimeout(r, ms));

function normalizeReview(r) {
  if (!r) return r;
  return {
    ...r,
    id:          r._id || r.id,
    rating:      r.rating  ?? r.stars  ?? 0,
    comment:     r.comment || r.testimonial || '',
    name:        r.name    || r.reviewer   || 'Anonymous',
    date:        r.date    || (r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''),
    avatarSeed:  r.avatarSeed  || r.name || r.reviewer || 'user',
    avatarBg:    r.avatarBg   || '6366f1',
    initials:    r.initials   || (r.name || r.reviewer || 'A')[0].toUpperCase(),
    sessionType: r.sessionType || 'Session',
  };
}

// ── MOCK DATA ─────────────────────────────────────────────────
const MOCK_REVIEWS = [
  { _id: 'rev_001', stars: 5, testimonial: 'Amar is an incredible mentor. He helped me level up my React skills in just 2 sessions!', reviewer: 'Priya S.', tags: ['💡 Clear explanations', '🚀 Boosted my confidence'], isVerified: true, createdAt: new Date('2026-03-10') },
  { _id: 'rev_002', stars: 5, testimonial: 'Best investment I made for my career. Got a 40% salary hike after following his roadmap.', reviewer: 'Rahul V.', tags: ['🎯 Actionable advice', '🔄 Will book again'], isVerified: true, createdAt: new Date('2026-03-05') },
  { _id: 'rev_003', stars: 4, testimonial: 'Very knowledgeable and patient. Explained complex system design concepts clearly.', reviewer: 'Ankit M.', tags: ['💡 Clear explanations'], isVerified: true, createdAt: new Date('2026-02-28') },
];

// ═══════════════════════════════════════════════════════════════
//  REVIEW SERVICE
// ═══════════════════════════════════════════════════════════════
export const reviewService = {
  async getMentorReviews(mentorId, { page = 1, limit = 10, sort = 'newest' } = {}) {
    if (API_CONFIG.MOCK) {
      await delay();
      return { reviews: MOCK_REVIEWS.map(normalizeReview), total: MOCK_REVIEWS.length, page, totalPages: 1 };
    }
    return apiClient.get(`/reviews/mentor/${mentorId}`, { params: { page, limit, sort } }).then(res => ({
      ...res,
      reviews: (res.reviews || []).map(normalizeReview),
    }));
  },

  async submitReview(data) {
    if (API_CONFIG.MOCK) {
      await delay();
      return { review: { ...data, _id: `rev_${Date.now()}`, isVerified: true, createdAt: new Date() } };
    }
    return apiClient.post('/reviews', data);
  },

  async getMyReviews() {
    if (API_CONFIG.MOCK) {
      await delay();
      return { reviews: MOCK_REVIEWS.slice(0, 1) };
    }
    return apiClient.get('/reviews/me');
  },
};

// ═══════════════════════════════════════════════════════════════
//  WALLET SERVICE
// ═══════════════════════════════════════════════════════════════
export const walletService = {
  async getWallet() {
    if (API_CONFIG.MOCK) {
      await delay();
      return {
        balance: 38450, totalEarned: 312400, totalPaidOut: 260000,
        transactions: [
          { type: 'credit', amountINR: 1360, description: 'Session earnings — Booking DB-2026-000001', date: new Date('2026-03-15') },
          { type: 'payout', amountINR: 10000, description: 'Payout to HDFC ****4321', date: new Date('2026-03-01') },
        ],
      };
    }
    return apiClient.get('/wallet/me');
  },

  async requestPayout(data) {
    if (API_CONFIG.MOCK) {
      await delay();
      return { message: '[MOCK] Payout requested.', payout: { ...data, status: 'requested' } };
    }
    return apiClient.post('/wallet/payout', data);
  },

  async getPayouts() {
    if (API_CONFIG.MOCK) {
      await delay();
      return { payouts: [{ _id: 'payout_001', amountINR: 10000, status: 'paid', requestedAt: new Date('2026-03-01') }] };
    }
    return apiClient.get('/wallet/payouts');
  },
};

// ═══════════════════════════════════════════════════════════════
//  CERTIFICATION SERVICE
// ═══════════════════════════════════════════════════════════════
export const certificationService = {
  async getCertifications() {
    if (API_CONFIG.MOCK) {
      await delay();
      return {
        certifications: [
          { _id: 'cert_001', slug: 'full-stack-web-development', title: 'Full Stack Web Development', track: 'Full Stack', durationWeeks: 8, priceINR: 15000, isActive: true },
          { _id: 'cert_002', slug: 'react-advanced', title: 'Advanced React Development', track: 'Frontend', durationWeeks: 6, priceINR: 12000, isActive: true },
        ],
      };
    }
    return apiClient.get('/certifications');
  },

  async getCertification(slug) {
    if (API_CONFIG.MOCK) {
      await delay();
      return { certification: { _id: 'cert_001', slug, title: 'Full Stack Web Development', track: 'Full Stack', durationWeeks: 8, priceINR: 15000, projects: [{ id: 'p1', title: 'Portfolio Site', week: 1 }, { id: 'p2', title: 'REST API', week: 3 }] } };
    }
    return apiClient.get(`/certifications/${slug}`);
  },

  async getMyEnrollments() {
    if (API_CONFIG.MOCK) {
      await delay();
      return { enrollments: [{ _id: 'enroll_001', status: 'in_progress', certificationId: { title: 'Full Stack Web Development' }, submissions: [] }] };
    }
    return apiClient.get('/certifications/enrollments/me');
  },

  async verifyCertificate(certNo) {
    if (API_CONFIG.MOCK) {
      await delay();
      return { valid: true, certNo, issuedTo: 'Priya Sharma', track: 'Full Stack Web Development', issuedAt: new Date('2026-03-20'), mentor: 'Amar Krishnan', devbrainsVerified: true };
    }
    return apiClient.get(`/certifications/certificates/${certNo}/verify`);
  },
};

// ═══════════════════════════════════════════════════════════════
//  EVENTS SERVICE
//  Shared in-memory store (mock mode) — single source of truth
//  for both EventsPage (public) and MyEvents (mentor dashboard).
// ═══════════════════════════════════════════════════════════════

// Lazily-initialised mutable store; deep-cloned from EVENTS so
// mutations never affect the original import.
let _store = null;
function getStore() {
  if (!_store) _store = EVENTS.map(e => ({ ...e }));
  return _store;
}

export const eventService = {
  // ── Public listing ──────────────────────────────────────────
  async getEvents() {
    if (API_CONFIG.MOCK) {
      await delay();
      return { events: getStore() };
    }
    // TODO §BACKEND: GET /api/events?page=1&limit=20&tag=React&free=true
    return apiClient.get('/events');
  },

  async getEvent(id) {
    if (API_CONFIG.MOCK) {
      await delay();
      return { event: getStore().find(e => e.id === id) || null };
    }
    return apiClient.get(`/events/${id}`);
  },

  // ── Mentor — own events ─────────────────────────────────────
  async getMyEvents(mentorId) {
    if (API_CONFIG.MOCK) {
      await delay();
      const store = getStore();
      return {
        events: mentorId != null
          ? store.filter(e => e.mentorId === mentorId)
          : store,
      };
    }
    // TODO §BACKEND: GET /api/events/my (mentor only, auth required)
    return apiClient.get('/events/my');
  },

  // ── Registration ────────────────────────────────────────────
  async registerForEvent(id) {
    if (API_CONFIG.MOCK) {
      await delay(300);
      const ev = getStore().find(e => e.id === id);
      if (ev && ev.registeredCount < ev.maxAttendees) {
        ev.registeredCount += 1;
      }
      return {
        message: '[MOCK] Registered successfully.',
        registeredCount: ev?.registeredCount ?? 0,
      };
    }
    // TODO §BACKEND: POST /api/events/:id/register
    // Authorization: Bearer <token>
    // → { registrationId, meetLink, calendarUrl }
    return apiClient.post(`/events/${id}/register`);
  },

  // ── Mentor CRUD ─────────────────────────────────────────────
  async createEvent(data) {
    if (API_CONFIG.MOCK) {
      await delay(400);
      const store = getStore();
      const newEv = {
        registeredCount: 0,
        status: 'upcoming',
        rating: null,
        totalRatings: 0,
        testimonials: [],
        recordingUrl: '',
        ...data,
        id: 'ev-new-' + Date.now(),
        socialLinks: {
          linkedin: data.linkedin || '',
          twitter:  data.twitter  || '',
        },
      };
      store.unshift(newEv);
      return { event: newEv };
    }
    // TODO §BACKEND: POST /api/events (mentor only)
    return apiClient.post('/events', data);
  },

  async updateEvent(id, data) {
    if (API_CONFIG.MOCK) {
      await delay(400);
      const store = getStore();
      const idx = store.findIndex(e => e.id === id);
      if (idx !== -1) {
        store[idx] = {
          ...store[idx],
          ...data,
          socialLinks: {
            linkedin: data.linkedin || store[idx].socialLinks?.linkedin || '',
            twitter:  data.twitter  || store[idx].socialLinks?.twitter  || '',
          },
        };
        return { event: store[idx] };
      }
      return { event: null };
    }
    // TODO §BACKEND: PUT /api/events/:id (mentor/admin only)
    return apiClient.put(`/events/${id}`, data);
  },

  async deleteEvent(id) {
    if (API_CONFIG.MOCK) {
      await delay(300);
      const store = getStore();
      const idx = store.findIndex(e => e.id === id);
      if (idx !== -1) store.splice(idx, 1);
      return { message: '[MOCK] Event deleted.' };
    }
    // TODO §BACKEND: DELETE /api/events/:id (mentor/admin only)
    return apiClient.delete(`/events/${id}`);
  },
};

// ═══════════════════════════════════════════════════════════════
//  FOLLOW SERVICE
// ═══════════════════════════════════════════════════════════════
export const followService = {
  async follow(mentorId) {
    if (API_CONFIG.MOCK) { await delay(); return { follow: { mentorId, isFavorite: false } }; }
    return apiClient.post('/follows', { mentorId });
  },
  async unfollow(mentorId) {
    if (API_CONFIG.MOCK) { await delay(); return { message: '[MOCK] Unfollowed.' }; }
    return apiClient.delete(`/follows/${mentorId}`);
  },
  async toggleFavorite(mentorId, isFavorite) {
    if (API_CONFIG.MOCK) { await delay(); return { follow: { mentorId, isFavorite } }; }
    return apiClient.put(`/follows/favorites/${mentorId}`, { isFavorite });
  },
  async getFollows() {
    if (API_CONFIG.MOCK) { await delay(); return { follows: [] }; }
    return apiClient.get('/follows');
  },
};
